import { reactive } from "vue";
import { Pet } from "../core/pet";

export type SwfPreloadStatus = "idle" | "loading" | "done";

/** 预热进度（响应式，供状态栏展示） */
export const swfPreloadState = reactive<{
    status: SwfPreloadStatus;
    total: number;
    done: number;
    failed: number;
}>({
    status: "idle",
    total: 0,
    done: 0,
    failed: 0,
});

/** 每场对局只预热一次 */
let started = false;
let preloadGeneration = 0;

/** 重置预热状态（新一场对局开始时调用） */
export function resetSwfPreload() {
    started = false;
    preloadGeneration += 1;
    swfPreloadState.status = "idle";
    swfPreloadState.total = 0;
    swfPreloadState.done = 0;
    swfPreloadState.failed = 0;
}

/**
 * 收集阵容中全部精灵的皮肤 SWF URL（按皮肤 id 去重）
 */
export function collectSkinUrls(pets: Pet[]): string[] {
    const seen = new Set<number>();
    const urls: string[] = [];
    for (const pet of pets) {
        for (const skin of pet.皮肤组) {
            if (seen.has(skin.id)) {
                continue;
            }
            seen.add(skin.id);
            urls.push(skin.getBattleAnimUrl());
        }
    }
    return urls;
}

/**
 * 将阵容中所有皮肤的 SWF 预热进浏览器 HTTP 缓存。
 * 每场对局只执行一次；失败项仅告警，不影响后续（Ruffle 仍会在切换时按需加载）。
 */
export async function warmSwfCache(pets: Pet[]): Promise<void> {
    if (started) {
        return;
    }
    const urls = collectSkinUrls(pets);
    if (urls.length === 0) {
        return;
    }
    started = true;
    const generation = preloadGeneration;
    swfPreloadState.status = "loading";
    swfPreloadState.total = urls.length;
    swfPreloadState.done = 0;
    swfPreloadState.failed = 0;

    await Promise.all(
        urls.map(async (url) => {
            try {
                const res = await fetch(url, { mode: "cors" });
                if (!res.ok) {
                    throw new Error(`HTTP ${res.status}`);
                }
                if (res.body) {
                    // 流式消费 body，完整写入 HTTP 缓存且不滞留整份数据在内存
                    const reader = res.body.getReader();
                    while (true) {
                        const { done } = await reader.read();
                        if (done) {
                            break;
                        }
                    }
                } else {
                    await res.arrayBuffer();
                }
                if (generation === preloadGeneration) {
                    swfPreloadState.done += 1;
                }
            } catch (err) {
                if (generation === preloadGeneration) {
                    swfPreloadState.failed += 1;
                }
                console.warn(`[swf-preload] 预热失败: ${url}`, err);
            }
        }),
    );

    if (generation === preloadGeneration) {
        swfPreloadState.status = "done";
    }
}
